import { useCallback, useMemo, useState } from "react";

import {
  Badge,
  Button,
  FontIcon,
  TableColumnDefinition,
  TableRowDefinition,
  TableWithDefaultFilters,
  Typography,
} from "@ezmid/components-library";
import {
  faBookOpen,
  faFingerprint,
  faLock,
  faLockOpen,
  faSortNumericDown,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import {
  EntityLesson,
  useDeleteLessonByIdMutation,
} from "../../api/lesson.generated";
import { AllRoutes } from "../../features/route/Route";
import { makeRoute } from "../../hooks/route";
import { truncateWithEllipsis } from "../../utils/stringUtils";
import { DeleteConfirmationModal } from "../modals/DeleteConfirmationModal";

type Props = {
  isFetching: boolean;
  lessons: Array<EntityLesson | undefined>;
  headerText: string;
  courseId?: string;
  refetch?: () => void;
};

type TableData = {
  id: string;
  name: string;
  order: number;
  access: string;
  isFree: boolean;
};

export function LessonsTable<T extends object>({
  isFetching,
  lessons,
  headerText,
  courseId,
  refetch,
}: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langCode = i18n.language.split("-")[0];

  const [deleteLesson, { isLoading: isDeleting }] =
    useDeleteLessonByIdMutation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const handleDeleteClick = useCallback(
    (lessonId: string, lessonName: string) => {
      setLessonToDelete({ id: lessonId, name: lessonName });
      setIsDeleteModalOpen(true);
    },
    []
  );

  const handleConfirmDelete = async () => {
    if (!lessonToDelete) return;

    try {
      await deleteLesson({ id: lessonToDelete.id }).unwrap();
      toast.success(t("app.lesson.delete.success"));
      setIsDeleteModalOpen(false);
      setLessonToDelete(null);
      if (refetch) {
        refetch();
      }
    } catch (error) {
      toast.error(t("app.lesson.delete.failed"));
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setLessonToDelete(null);
    }
  };

  const columns: Array<TableColumnDefinition<TableData>> = useMemo(
    () => [
      {
        Header: t("table.header.name"),
        id: "name",
        accessor: "name",
        Cell: ({ row }: TableRowDefinition<T>) => {
          return (
            <Typography variant="body1">
              {truncateWithEllipsis(row?.original?.name || "", 18)}
            </Typography>
          );
        },
        maxWidth: 200,
      },
      {
        Header: t("table.header.id"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={4}
                customIcon={<FontAwesomeIcon size="lg" icon={faFingerprint} />}
              />
            }
          >
            {row?.original?.id?.toUpperCase().substr(0, 8) || ""}
          </Badge>
        ),
        id: "id",
        accessor: "id",
        width: "180px",
        maxWidth: "180px",
      },
      {
        Header: t("table.header.order"),
        Cell: ({ row }: TableRowDefinition<T>) => (
          <Badge
            variant="pill"
            leftIcon={
              <FontIcon
                marginTop={3}
                customIcon={
                  <FontAwesomeIcon size="lg" icon={faSortNumericDown} />
                }
              />
            }
          >
            {row?.original?.order ?? ""}
          </Badge>
        ),
        id: "order",
        accessor: "order",
        width: "120px",
        maxWidth: "120px",
      },
      {
        Header: t("table.header.access"),
        Cell: ({ row }: TableRowDefinition<T>) => {
          const isFree = row?.original?.isFree;
          const status = isFree ? "success" : "warning";
          const icon = isFree ? faLockOpen : faLock;

          return (
            <Badge
              status={status}
              variant="pill"
              leftIcon={
                <FontIcon
                  marginTop={3}
                  customIcon={<FontAwesomeIcon size="lg" icon={icon} />}
                />
              }
            >
              {row?.original?.access || ""}
            </Badge>
          );
        },
        id: "access",
        accessor: "access",
        width: "140px",
        maxWidth: "140px",
      },
      {
        Header: "",
        Cell: ({ row }: TableRowDefinition<T>) => (
          <div className="flex justify-end pr-2">
            <Button
              variant="danger"
              onClick={() => {
                handleDeleteClick(row?.original.id, row?.original.name);
              }}
            >
              {t("button.delete")}
            </Button>
          </div>
        ),
        id: "actions",
        accessor: "id",
        width: "150px",
      },
    ],
    [t, handleDeleteClick]
  );

  const memoizedData: Array<TableData> = useMemo(
    () =>
      lessons
        .filter((lesson): lesson is EntityLesson => lesson !== undefined)
        .map((lesson) => {
          const translatedName = lesson?.translations?.find(
            (translation) => translation?.language === langCode
          )?.name;
          return {
            id: lesson?.id || "",
            name: translatedName || "",
            order: lesson?.order ?? 0,
            isFree: lesson?.isFree ?? false,
            access: lesson?.isFree
              ? t("lesson.access.free")
              : t("lesson.access.paid"),
          };
        }),
    [lessons, t, langCode]
  );

  return (
    <div>
      <TableWithDefaultFilters
        tableData={memoizedData}
        tableColumns={columns}
        showPagination={false}
        showToolbar={false}
        headerIcon={
          <FontIcon
            marginTop={4}
            customIcon={<FontAwesomeIcon icon={faBookOpen} />}
          />
        }
        headerText={headerText}
        onRowSelect={(row) =>
          navigate(
            makeRoute(AllRoutes.LESSON_DETAIL, { id: row.original?.id }),
            {
              state: { courseId },
            }
          )
        }
      />
      <DeleteConfirmationModal
        isModalOpen={isDeleteModalOpen}
        onModalClose={handleCloseDeleteModal}
        onConfirmDelete={handleConfirmDelete}
        itemName={lessonToDelete?.name || ""}
        isDeleting={isDeleting}
        titleKey="modal.deleteLesson.heading"
        confirmMessageKey="app.lesson.delete.confirm"
      />
    </div>
  );
}
